/**
 * W65 — New Opportunity page
 * /opportunity/new or /account/:accountId/opportunity/new
 * Creates a new opportunity for an account.
 */
import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { getSession } from "@/lib/session";
import { Header, Footer } from "@/components/Shell";
import { Link } from "wouter";

const SOURCE_CHANNELS = [
  { value: "ae_self_sourced",    label: "AE Self-sourced" },
  { value: "bdr_sourced",        label: "BDR-sourced" },
  { value: "marketing_inbound",  label: "Marketing Inbound" },
  { value: "partner",            label: "Partner" },
  { value: "expansion",          label: "Expansion" },
  { value: "referral",           label: "Referral" },
];

export default function NewOpportunity() {
  const params = useParams<{ accountId?: string }>();
  const [, setLoc] = useLocation();

  const [accountId, setAccountId] = useState(params.accountId ?? "");
  const [accountName, setAccountName] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [name, setName] = useState("");
  const [sourceChannel, setSourceChannel] = useState("ae_self_sourced");
  const [estCloseDate, setEstCloseDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  // Load accounts for picker if no accountId pre-set
  useEffect(() => {
    if (!params.accountId) {
      fetch("/api/accounts?limit=100", { headers: authHeader })
        .then((r) => r.json())
        .then((j) => setAccounts((j?.accounts ?? j?.data ?? []).map((a: any) => ({ id: String(a.id), name: a.name }))))
        .catch(() => {});
    } else {
      setAccountId(params.accountId);
      // Try to resolve account name
      fetch(`/api/accounts/${params.accountId}`, { headers: authHeader })
        .then((r) => r.json())
        .then((j) => setAccountName(j?.account?.name ?? j?.name ?? null))
        .catch(() => {});
    }
  }, [params.accountId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!accountId) { setError("Select an account."); return; }
    if (!name.trim()) { setError("Opportunity name required."); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/opportunities", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          account_id: accountId,
          name: name.trim(),
          source_channel: sourceChannel || null,
          est_close_date: estCloseDate || null,
          notes: notes.trim() || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error ?? "Failed to create opportunity");
      }
      const j = await res.json();
      const newId = j.opportunity?.id;
      if (newId) {
        setLoc(`/opportunity/${newId}`);
      } else {
        setLoc("/opportunities");
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const labelSt: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: "#6B7280", display: "block", marginBottom: 4,
  };
  const inputSt: React.CSSProperties = {
    width: "100%", boxSizing: "border-box" as const, border: "1px solid #C9C9CC",
    borderRadius: 5, padding: "8px 10px", fontSize: 13, fontFamily: "inherit",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}>
      <Header />

      <div style={{ maxWidth: 560, margin: "32px auto", padding: "0 16px" }}>
        <div style={{ marginBottom: 16 }}>
          <Link href="/opportunities" style={{ fontSize: 13, color: "#0B0B0F", textDecoration: "none" }}>
            ← Opportunities
          </Link>
        </div>

        <div style={{ background: "#FFFFFF", borderRadius: 10, border: "1px solid #E5E5E7", padding: 28 }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0B0B0F", margin: "0 0 20px" }}>
            New Opportunity
          </h1>

          {error && (
            <div style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 12, padding: "8px 12px", borderRadius: 5, marginBottom: 14 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Account */}
            <div>
              <label style={labelSt}>ACCOUNT <span style={{ color: "#DC2626" }}>*</span></label>
              {params.accountId ? (
                <div style={{ fontSize: 14, fontWeight: 600, color: "#0B0B0F", padding: "7px 0" }}>
                  {accountName ?? params.accountId}
                </div>
              ) : (
                <select
                  required
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  style={inputSt}
                >
                  <option value="">Select account…</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Name */}
            <div>
              <label style={labelSt}>OPPORTUNITY NAME <span style={{ color: "#DC2626" }}>*</span></label>
              <input
                required
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Rivian — Platform Subscription 2026"
                style={inputSt}
              />
            </div>

            {/* Source channel */}
            <div>
              <label style={labelSt}>SOURCE CHANNEL</label>
              <select
                value={sourceChannel}
                onChange={(e) => setSourceChannel(e.target.value)}
                style={inputSt}
              >
                {SOURCE_CHANNELS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Est close date */}
            <div>
              <label style={labelSt}>EST. CLOSE DATE</label>
              <input
                type="date"
                value={estCloseDate}
                onChange={(e) => setEstCloseDate(e.target.value)}
                style={inputSt}
              />
            </div>

            {/* Notes */}
            <div>
              <label style={labelSt}>NOTES</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context…"
                style={{ ...inputSt, minHeight: 72, resize: "vertical" }}
              />
            </div>

            <div style={{ display: "flex", gap: 10, paddingTop: 4 }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  background: "#0B0B0F", color: "#FFFFFF", border: "none", borderRadius: 6,
                  padding: "10px 22px", fontSize: 14, fontWeight: 600,
                  cursor: saving ? "not-allowed" : "pointer", flex: 1, opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? "Creating…" : "Create Opportunity"}
              </button>
              <Link
                href="/opportunities"
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "none", border: "1px solid #C9C9CC", borderRadius: 6,
                  padding: "10px 18px", fontSize: 13, color: "#525258", textDecoration: "none",
                }}
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
